import { t } from 'i18next';
import { useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';

import DividerWithText from '~components/DividerWithText/DividerWithText';
import { ClearFiltersHint } from '~components/Filters/ClearFiltersHint';
import { SearchParams } from '~constants/enums';
import { persistDateRange } from '~utils/dateRangePersistence';

import {
  APPROVED_TOGGLE_QS_PARAM,
  IN_SETTLEMENT_TOGGLE_QS_PARAM,
  UNAPPROVED_TOGGLE_QS_PARAM,
} from '../constants';
import DriverPayQueryContext from '../DriverPayQueryContext';
import { driverPayDateRangeStorageKey } from '../utils';

export default function OrdersListFooter() {
  const {
    clearAllFiltersExceptSearch,
    data,
    fetchReason,
    hasMore,
    hasUnfilteredResults,
  } = useContext(DriverPayQueryContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleClearFiltersKeepSearch = useCallback(() => {
    clearAllFiltersExceptSearch();

    // The user chose "All dates": persist explicit nulls so the paramless URL
    // this handler produces below is not re-hydrated from the previous range.
    persistDateRange(
      driverPayDateRangeStorageKey(searchParams.get(SearchParams.INVOICE_CATEGORY)),
      { endDate: null, startDate: null },
    );

    // Functional updater (never a captured params object — TRE-15462). The date and
    // state-toggle params clear together with the context state so the URL never
    // advertises a filter the query stopped sending; `tab`/`category` survive.
    setSearchParams((params) => {
      params.delete(SearchParams.START_DATE);
      params.delete(SearchParams.END_DATE);
      params.delete(UNAPPROVED_TOGGLE_QS_PARAM);
      params.delete(APPROVED_TOGGLE_QS_PARAM);
      params.delete(IN_SETTLEMENT_TOGGLE_QS_PARAM);
      return params;
    });
  }, [clearAllFiltersExceptSearch, setSearchParams, searchParams]);

  if (hasMore || fetchReason) {
    return null;
  }

  return (
    <>
      <DividerWithText
        text={
          data.length === 0
            ? t('common.no_data')
            : t('dispatch.dispatch_v2.all_orders_loaded')
        }
      />

      {data.length === 0 && hasUnfilteredResults && (
        <ClearFiltersHint onClearFilters={handleClearFiltersKeepSearch} />
      )}
    </>
  );
}
